import { useMemo, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  CalendarRange,
  Repeat,
  Dumbbell,
} from "lucide-react"
import type { Routine } from "../../types"

const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
]
const DAY_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"]
const START_HOUR = 6
const END_HOUR = 22
const HOUR_HEIGHT = 56
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i)

type ViewMode = "week" | "month"

// YYYY-MM-DD en hora local (evita el corrimiento UTC de toISOString).
function localDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function getWeekDays(date: Date): Date[] {
  const d = new Date(date)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day // semana inicia en lunes
  d.setDate(d.getDate() + diff)
  return Array.from({ length: 7 }, (_, i) => {
    const r = new Date(d)
    r.setDate(d.getDate() + i)
    return r
  })
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

// ¿Esta rutina cae en este día? Coincide por fecha concreta (scheduled_date) o,
// si no tiene fecha (rutina antigua), por día de semana (scheduled_day).
function routineOnDay(r: Routine, day: Date): boolean {
  const dayStr = localDateStr(day)
  if (r.scheduled_date) return r.scheduled_date.slice(0, 10) === dayStr
  return r.scheduled_day === day.getDay()
}

function hourLabel(h?: number) {
  return h != null ? `${String(h).padStart(2, "0")}:00` : ""
}

export default function ProgramCalendar({
  routines,
  onSelectRoutine,
  onAssign,
}: {
  routines: Routine[]
  onSelectRoutine: (r: Routine) => void
  onAssign: (date: Date, hour?: number) => void
}) {
  const [viewMode, setViewMode] = useState<ViewMode>("week")
  const [currentDate, setCurrentDate] = useState(new Date())
  const now = new Date()

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate])

  function navigate(dir: -1 | 1) {
    setCurrentDate((prev) => {
      const d = new Date(prev)
      if (viewMode === "month") d.setMonth(d.getMonth() + dir)
      else d.setDate(d.getDate() + dir * 7)
      return d
    })
  }

  const gridHeight = (END_HOUR - START_HOUR) * HOUR_HEIGHT
  const nowOffset =
    now.getHours() >= START_HOUR && now.getHours() < END_HOUR
      ? ((now.getHours() - START_HOUR) * 60 + now.getMinutes()) / 60 * HOUR_HEIGHT
      : null

  const weekLabel = (() => {
    const start = weekDays[0]
    const end = weekDays[6]
    return `${start.getDate()} ${MONTH_NAMES[start.getMonth()].slice(0, 3)} — ${end.getDate()} ${MONTH_NAMES[end.getMonth()].slice(0, 3)} ${end.getFullYear()}`
  })()

  return (
    <div className="space-y-4">
      {/* Barra de navegación */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="rounded-lg border border-white/10 p-2 text-muted hover:text-white hover:border-white/20 transition"
          >
            <ChevronLeft size={16} />
          </button>
          <h3 className="min-w-[190px] text-center text-sm font-semibold text-white">
            {viewMode === "month" ? `${MONTH_NAMES[month]} ${year}` : weekLabel}
          </h3>
          <button
            onClick={() => navigate(1)}
            className="rounded-lg border border-white/10 p-2 text-muted hover:text-white hover:border-white/20 transition"
          >
            <ChevronRight size={16} />
          </button>
          <button
            onClick={() => setCurrentDate(new Date())}
            className="ml-2 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-muted hover:text-white hover:border-white/20 transition"
          >
            Hoy
          </button>
        </div>

        {/* Toggle vista */}
        <div className="flex rounded-lg border border-white/10 overflow-hidden">
          <button
            onClick={() => setViewMode("week")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition ${
              viewMode === "week" ? "bg-primary/10 text-primary" : "text-muted hover:text-white"
            }`}
          >
            <CalendarRange size={14} />
            Semana
          </button>
          <button
            onClick={() => setViewMode("month")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition ${
              viewMode === "month" ? "bg-primary/10 text-primary" : "text-muted hover:text-white"
            }`}
          >
            <CalendarDays size={14} />
            Mes
          </button>
        </div>
      </div>

      {viewMode === "week" ? (
        <AnimatePresence mode="wait">
          <motion.div
            key={weekDays[0].toISOString()}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="max-h-[640px] overflow-auto rounded-xl border border-white/10"
          >
            <div className="min-w-[700px]">
              {/* Encabezado de días (sticky) */}
              <div className="sticky top-0 z-20 grid grid-cols-[56px_repeat(7,1fr)] border-b border-white/10 bg-[#0d0f14]">
                <div className="border-r border-white/10" />
                {weekDays.map((day, i) => {
                  const isToday = isSameDay(day, now)
                  return (
                    <div
                      key={i}
                      className={`border-r border-white/5 py-2 text-center ${isToday ? "bg-primary/5" : ""}`}
                    >
                      <p className="text-[11px] text-muted">{DAY_LABELS[i]}</p>
                      <p
                        className={`mx-auto mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold ${
                          isToday ? "bg-primary text-black" : "text-white"
                        }`}
                      >
                        {day.getDate()}
                      </p>
                    </div>
                  )
                })}
              </div>

              {/* Cuerpo: regla horaria + columnas por día */}
              <div className="grid grid-cols-[56px_repeat(7,1fr)]">
                {/* Regla horaria */}
                <div className="relative border-r border-white/10" style={{ height: gridHeight }}>
                  {HOURS.map((h, idx) => (
                    <div
                      key={h}
                      className="absolute right-1.5 -translate-y-1/2 text-[10px] text-muted"
                      style={{ top: idx * HOUR_HEIGHT }}
                    >
                      {String(h).padStart(2, "0")}:00
                    </div>
                  ))}
                </div>

                {/* Columnas por día */}
                {weekDays.map((day, i) => {
                  const isToday = isSameDay(day, now)
                  const dayRoutines = routines.filter((r) => routineOnDay(r, day))
                  return (
                    <div
                      key={i}
                      className={`relative border-r border-white/5 ${isToday ? "bg-primary/[0.03]" : ""}`}
                      style={{ height: gridHeight }}
                    >
                      {/* Celdas horarias (click para asignar) */}
                      {HOURS.map((h, idx) => (
                        <button
                          key={h}
                          onClick={() => onAssign(day, h)}
                          className="absolute inset-x-0 border-t border-white/5 hover:bg-primary/5 transition"
                          style={{ top: idx * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                          title={`Asignar rutina · ${DAY_LABELS[i]} ${hourLabel(h)}`}
                        />
                      ))}

                      {/* Indicador de "ahora" */}
                      {isToday && nowOffset !== null && (
                        <div className="pointer-events-none absolute inset-x-0 z-10" style={{ top: nowOffset }}>
                          <div className="h-px bg-red-500" />
                          <div className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-red-500" />
                        </div>
                      )}

                      {/* Bloques de rutina */}
                      {dayRoutines.map((r) => {
                        const sh = r.start_hour ?? START_HOUR
                        const eh = r.end_hour ?? sh + 1
                        if (eh <= START_HOUR || sh >= END_HOUR) return null
                        const top = (sh - START_HOUR) * HOUR_HEIGHT
                        const height = Math.max(22, (eh - sh) * HOUR_HEIGHT)
                        return (
                          <button
                            key={r.id}
                            onClick={() => onSelectRoutine(r)}
                            className="absolute left-0.5 right-0.5 z-10 overflow-hidden rounded-md border border-primary/50 bg-primary/20 px-1.5 py-1 text-left transition hover:z-20 hover:bg-primary/30 hover:shadow-lg"
                            style={{ top: top + 1, height: height - 2 }}
                          >
                            <div className="flex items-center gap-1">
                              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                              <p className="truncate text-[10px] font-semibold text-primary">{r.name}</p>
                              {r.series_id && <Repeat size={9} className="shrink-0 text-primary/60" />}
                            </div>
                            {height > 34 && (
                              <p className="mt-0.5 truncate text-[9px] text-muted">
                                {hourLabel(r.start_hour)}
                                {r.end_hour != null ? `–${hourLabel(r.end_hour)}` : ""} ·{" "}
                                {r.routine_exercises.length} ej.
                              </p>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      ) : (
        <MonthGrid
          year={year}
          month={month}
          routines={routines}
          onSelectRoutine={onSelectRoutine}
          onAssign={onAssign}
        />
      )}

      {/* Leyenda */}
      <div className="flex flex-wrap gap-4">
        <span className="flex items-center gap-1.5 text-xs text-muted">
          <span className="h-2.5 w-2.5 rounded-full bg-primary" /> Rutina
        </span>
        <span className="flex items-center gap-1.5 text-xs text-muted">
          <Repeat size={12} className="text-primary/60" /> Parte de una serie
        </span>
        <span className="flex items-center gap-1.5 text-xs text-muted">
          Click en un hueco para asignar · click en una rutina para editar
        </span>
      </div>
    </div>
  )
}

// ── Vista de Mes ───────────────────────────────────────────────────────────
function MonthGrid({
  year,
  month,
  routines,
  onSelectRoutine,
  onAssign,
}: {
  year: number
  month: number
  routines: Routine[]
  onSelectRoutine: (r: Routine) => void
  onAssign: (date: Date) => void
}) {
  const now = new Date()
  // Construir la grilla (lunes a domingo) que cubre el mes.
  const first = new Date(year, month, 1)
  const startOffset = (first.getDay() + 6) % 7 // lunes = 0
  const gridStart = new Date(year, month, 1 - startOffset)
  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart)
    d.setDate(gridStart.getDate() + i)
    return d
  })

  return (
    <div className="overflow-hidden rounded-xl border border-white/10">
      <div className="grid grid-cols-7 border-b border-white/10 bg-[#0d0f14]">
        {DAY_LABELS.map((l) => (
          <div key={l} className="py-2 text-center text-[11px] text-muted">{l}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          const inMonth = day.getMonth() === month
          const isToday = isSameDay(day, now)
          const dayRoutines = routines.filter((r) => routineOnDay(r, day))
          return (
            <button
              key={i}
              onClick={() => onAssign(day)}
              className={`min-h-[92px] border-b border-r border-white/5 p-1.5 text-left align-top transition hover:bg-primary/5 ${
                inMonth ? "" : "opacity-40"
              }`}
            >
              <span
                className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                  isToday ? "bg-primary text-black" : "text-white"
                }`}
              >
                {day.getDate()}
              </span>
              <div className="mt-1 space-y-0.5">
                {dayRoutines.slice(0, 3).map((r) => (
                  <span
                    key={r.id}
                    onClick={(e) => { e.stopPropagation(); onSelectRoutine(r) }}
                    className="flex items-center gap-1 truncate rounded bg-primary/15 px-1 py-0.5 text-[9px] text-primary hover:bg-primary/25"
                  >
                    <Dumbbell size={8} className="shrink-0" />
                    <span className="truncate">{r.name}</span>
                    {r.series_id && <Repeat size={8} className="shrink-0 text-primary/60" />}
                  </span>
                ))}
                {dayRoutines.length > 3 && (
                  <span className="block px-1 text-[9px] text-muted">+{dayRoutines.length - 3} más</span>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
